import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { trackLanguageSelected } from '../lib/analytics';
import flagCat from '../assets/flag-cat.png';
import flagEs from '../assets/flag-es.png';
import flagEn from '../assets/flag-en.png';
import flagFr from '../assets/flag-fr.png';

const languages = [
  { code: 'cat', flag: flagCat, name: 'Catal\u00e0' },
  { code: 'es', flag: flagEs, name: 'Espa\u00f1ol' },
  { code: 'en', flag: flagEn, name: 'English' },
  { code: 'fr', flag: flagFr, name: 'Fran\u00e7ais' },
];

export const LanguageSelector = () => {
  const { setLanguage } = useLanguage();

  return (
    <div
      data-testid="language-selector-screen"
      style={{
        background: 'linear-gradient(180deg, #E7CFA3 0%, #D2B48C 40%, #C19A6B 100%)',
        minHeight: '100dvh',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Kraft grain */}
      <div
        style={{
          position: 'absolute', inset: 0,
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          opacity: 0.06, mixBlendMode: 'multiply', pointerEvents: 'none',
        }}
      />

      {/* Main container */}
      <div
        style={{
          maxWidth: 420, margin: '0 auto', padding: '40px 24px 180px',
          display: 'flex', flexDirection: 'column',
          minHeight: '100dvh', position: 'relative', zIndex: 1,
        }}
      >
        {/* ── TOP: Branding ── */}
        <div style={{ textAlign: 'center', paddingTop: '12vh', animation: 'fadeInUp 0.6s ease 0.1s both' }}>
          <h1
            data-testid="pont10-title"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 'clamp(56px, 11vw, 96px)',
              letterSpacing: 2.4,
              color: '#3A2E1F',
              fontWeight: 600,
              margin: 0,
              lineHeight: 1,
            }}
          >
            PONT<span style={{ color: '#C89B3C', textShadow: '0 0 16px rgba(200,155,60,0.4)' }}>10</span>
          </h1>

          <p
            style={{
              fontSize: 'clamp(22px, 4.5vw, 36px)',
              marginTop: 16,
              color: 'rgba(58,46,31,0.9)',
              fontStyle: 'italic',
              fontFamily: "'Playfair Display', serif",
              animation: 'fadeInUp 0.6s ease 0.2s both',
            }}
          >
            {"\u00c9s sentir Besal\u00fa"}
          </p>

          <div
            style={{
              width: 120, height: 4,
              background: 'linear-gradient(90deg, rgba(200,155,60,0) 0%, rgba(200,155,60,0.8) 50%, rgba(200,155,60,0) 100%)',
              margin: '20px auto 0',
            }}
          />
        </div>

        {/* ── CENTER: Language grid ── */}
        <div style={{ marginTop: 40, animation: 'fadeInUp 0.6s ease 0.3s both' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            {languages.map((lang, i) => (
              <button
                key={lang.code}
                onClick={() => { trackLanguageSelected(lang.code); setLanguage(lang.code); }}
                data-testid={`language-btn-${lang.code}`}
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
                  borderRadius: 14, padding: 18,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  border: '1px solid rgba(255,255,255,0.28)',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.15)',
                  cursor: 'pointer', transition: 'all 0.25s ease',
                  outline: 'none', WebkitTapHighlightColor: 'transparent',
                  animation: `fadeInUp 0.5s ease ${0.3 + i * 0.06}s both`,
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                  e.currentTarget.style.background = 'rgba(255,255,255,0.14)';
                  e.currentTarget.style.boxShadow = '0 16px 35px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.15)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                  e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.15)';
                }}
                onTouchStart={e => {
                  e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                  e.currentTarget.style.background = 'rgba(255,255,255,0.14)';
                  e.currentTarget.style.boxShadow = '0 16px 35px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.15)';
                }}
                onTouchEnd={e => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                  e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.15)';
                }}
              >
                <img
                  src={lang.flag} alt={lang.name}
                  style={{
                    width: 42, height: 42, borderRadius: '50%', objectFit: 'cover',
                    marginBottom: 10, boxShadow: '0 6px 14px rgba(0,0,0,0.18)',
                  }}
                />
                <span style={{ fontSize: 14, color: '#3A2E1F', fontWeight: 500, fontFamily: "'DM Sans', sans-serif", letterSpacing: 0.3 }}>
                  {lang.name}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* ── BOTTOM: Footer moved outside main container so the tagline
              stays on a single line regardless of the inner column width. ── */}
      </div>

      {/* ── FOOTER (full width, sits over the gradient) ── */}
      <div
        style={{
          position: 'absolute', left: 0, right: 0, bottom: 0,
          padding: '0 16px 60px',
          textAlign: 'center',
          animation: 'fadeInUp 0.6s ease 0.4s both',
          zIndex: 1,
          pointerEvents: 'none',
        }}
      >
        <svg
          viewBox="0 0 300 80"
          className="pont10-bridge"
          style={{ width: 200, margin: '0 auto 16px', display: 'block', opacity: 0.35 }}
        >
          {/* Arcs of the bridge — each path animates its dash to draw in. */}
          <path className="b-arc b-arc-1" d="M30 70 Q60 40, 90 70" stroke="#3A2E1F" strokeWidth="2" fill="none"/>
          <path className="b-arc b-arc-2" d="M90 70 Q120 40, 150 70" stroke="#3A2E1F" strokeWidth="2" fill="none"/>
          <path className="b-arc b-arc-3" d="M150 70 Q180 40, 210 70" stroke="#3A2E1F" strokeWidth="2" fill="none"/>
          <path className="b-arc b-arc-4" d="M210 70 Q240 40, 270 70" stroke="#3A2E1F" strokeWidth="2" fill="none"/>
          {/* Deck and tower come in last */}
          <line className="b-deck" x1="20" y1="38" x2="280" y2="38" stroke="#3A2E1F" strokeWidth="2"/>
          <rect className="b-tower" x="140" y="10" width="20" height="28" stroke="#3A2E1F" strokeWidth="1.5" fill="none"/>
          <rect className="b-tower-top" x="143" y="2" width="14" height="10" stroke="#3A2E1F" strokeWidth="1.5" fill="none"/>
          <line className="b-pillar b-pillar-1" x1="60" y1="38" x2="60" y2="70" stroke="#3A2E1F" strokeWidth="2"/>
          <line className="b-pillar b-pillar-2" x1="120" y1="38" x2="120" y2="70" stroke="#3A2E1F" strokeWidth="2"/>
          <line className="b-pillar b-pillar-3" x1="180" y1="38" x2="180" y2="70" stroke="#3A2E1F" strokeWidth="2"/>
          <line className="b-pillar b-pillar-4" x1="240" y1="38" x2="240" y2="70" stroke="#3A2E1F" strokeWidth="2"/>
        </svg>
        <p
          style={{
            fontSize: 'clamp(13px, 2.75vw, 22px)',
            letterSpacing: 'clamp(1.4px, 0.32vw, 2.8px)',
            color: 'rgba(58,46,31,0.65)',
            fontWeight: 500,
            margin: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {"Producte Local \u00b7 Artes\u00e0 \u00b7 Amb Hist\u00f2ria"}
        </p>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Bridge — line-by-line drawing animation. Each segment uses a
           dash length comfortably larger than the longest stroke (≈260px
           for the deck) so the final state shows a continuous line. */
        .pont10-bridge .b-arc,
        .pont10-bridge .b-deck,
        .pont10-bridge .b-pillar,
        .pont10-bridge .b-tower,
        .pont10-bridge .b-tower-top {
          stroke-dasharray: 500;
          stroke-dashoffset: 500;
          animation: drawStroke 1.1s cubic-bezier(0.65, 0, 0.35, 1) forwards;
        }
        .pont10-bridge .b-arc-1     { animation-delay: 0.50s; }
        .pont10-bridge .b-arc-2     { animation-delay: 0.70s; }
        .pont10-bridge .b-arc-3     { animation-delay: 0.90s; }
        .pont10-bridge .b-arc-4     { animation-delay: 1.10s; }
        .pont10-bridge .b-deck      { animation-delay: 0.30s; animation-duration: 1.3s; }
        .pont10-bridge .b-pillar-1  { animation-delay: 1.30s; animation-duration: 0.6s; }
        .pont10-bridge .b-pillar-2  { animation-delay: 1.40s; animation-duration: 0.6s; }
        .pont10-bridge .b-pillar-3  { animation-delay: 1.50s; animation-duration: 0.6s; }
        .pont10-bridge .b-pillar-4  { animation-delay: 1.60s; animation-duration: 0.6s; }
        .pont10-bridge .b-tower     { animation-delay: 1.70s; animation-duration: 0.5s; }
        .pont10-bridge .b-tower-top { animation-delay: 1.95s; animation-duration: 0.4s; }

        @keyframes drawStroke {
          to { stroke-dashoffset: 0; }
        }

        @media (prefers-reduced-motion: reduce) {
          .pont10-bridge .b-arc,
          .pont10-bridge .b-deck,
          .pont10-bridge .b-pillar,
          .pont10-bridge .b-tower,
          .pont10-bridge .b-tower-top {
            stroke-dashoffset: 0;
            animation: none;
          }
        }
      `}</style>
    </div>
  );
};

export default LanguageSelector;
